import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Upload, Trash2, RefreshCw, AlertCircle, CheckCircle, Clock, Image as ImageIcon, Video } from 'lucide-react'
import api from '../services/api'

interface Draft {
  id: string
  mediaUrl: string
  mediaType: 'photo' | 'video'
  caption: string
  location?: {
    latitude: number
    longitude: number
    city: string
    state: string
    country: string
  }
  createdAt: number
  fileSize: number
  status: 'pending' | 'uploading' | 'failed' | 'completed'
  uploadProgress?: number
  error?: string
}

export default function Drafts() {
  const navigate = useNavigate()
  const [drafts, setDrafts] = useState<Draft[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedDrafts, setSelectedDrafts] = useState<Set<string>>(new Set())
  const [isSelectionMode, setIsSelectionMode] = useState(false)

  useEffect(() => {
    loadDrafts()
  }, [])

  const loadDrafts = async () => {
    try {
      setIsLoading(true)
      const result = await api.getDrafts()

      if (result.success && result.drafts) {
        setDrafts(result.drafts)
      }
    } catch (error) {
      console.error('Error loading drafts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleSelect = (draftId: string) => {
    const newSelected = new Set(selectedDrafts)
    if (newSelected.has(draftId)) {
      newSelected.delete(draftId)
    } else {
      newSelected.add(draftId)
    }
    setSelectedDrafts(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedDrafts.size === drafts.length) {
      setSelectedDrafts(new Set())
    } else {
      setSelectedDrafts(new Set(drafts.map(d => d.id)))
    }
  }

  const handleUploadDraft = async (draftId: string) => {
    try {
      const result = await api.uploadDraft(draftId)

      if (result.success) {
        loadDrafts() // Reload to update status
      } else {
        alert(result.message || 'Failed to upload draft')
      }
    } catch (error) {
      console.error('Error uploading draft:', error)
      alert('Failed to upload draft')
    }
  }

  const handleUploadSelected = async () => {
    for (const draftId of selectedDrafts) {
      await handleUploadDraft(draftId)
    }
    setSelectedDrafts(new Set())
    setIsSelectionMode(false)
  }

  const handleDeleteDraft = async (draftId: string) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) {
      return
    }

    try {
      const result = await api.deleteDraft(draftId)

      if (result.success) {
        setDrafts(drafts.filter(d => d.id !== draftId))
        selectedDrafts.delete(draftId)
        setSelectedDrafts(new Set(selectedDrafts))
      } else {
        alert(result.message || 'Failed to delete draft')
      }
    } catch (error) {
      console.error('Error deleting draft:', error)
      alert('Failed to delete draft')
    }
  }

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedDrafts.size} drafts?`)) {
      return
    }

    for (const draftId of selectedDrafts) {
      await handleDeleteDraft(draftId)
    }
    setSelectedDrafts(new Set())
    setIsSelectionMode(false)
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const getStatusBadge = (draft: Draft) => {
    switch (draft.status) {
      case 'uploading':
        return (
          <div className="flex items-center gap-1 text-xs text-blue-600">
            <RefreshCw className="w-3 h-3 animate-spin" />
            {draft.uploadProgress}%
          </div>
        )
      case 'failed':
        return (
          <div className="flex items-center gap-1 text-xs text-red-600">
            <AlertCircle className="w-3 h-3" />
            Failed
          </div>
        )
      case 'completed':
        return (
          <div className="flex items-center gap-1 text-xs text-green-600">
            <CheckCircle className="w-3 h-3" />
            Posted
          </div>
        )
      default:
        return (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Clock className="w-3 h-3" />
            Draft
          </div>
        )
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onrecord-orange"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white bg-opacity-90 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-700 hover:text-onrecord-orange rounded-full p-1 hover:bg-orange-50"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Drafts</h1>
              <p className="text-xs text-gray-500">{drafts.length} saved</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {!isSelectionMode ? (
              <button
                onClick={() => setIsSelectionMode(true)}
                className="px-4 py-2 text-sm font-medium text-onrecord-orange hover:bg-orange-50 rounded-lg transition-colors"
              >
                Select
              </button>
            ) : (
              <>
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {selectedDrafts.size === drafts.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  onClick={() => {
                    setIsSelectionMode(false)
                    setSelectedDrafts(new Set())
                  }}
                  className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Selection Actions Bar */}
        {isSelectionMode && selectedDrafts.size > 0 && (
          <div className="px-4 py-3 bg-orange-50 border-t border-orange-100 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {selectedDrafts.size} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleUploadSelected}
                className="flex items-center gap-2 px-4 py-2 bg-onrecord-orange hover:bg-onrecord-orange-dark text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Upload className="w-4 h-4" />
                Upload
              </button>
              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drafts Grid */}
      <div className="p-4">
        {drafts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ImageIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Drafts</h2>
            <p className="text-gray-600 mb-6">Your saved drafts will appear here</p>
            <button
              onClick={() => navigate('/create')}
              className="px-6 py-3 bg-onrecord-orange hover:bg-onrecord-orange-dark text-white rounded-xl font-semibold transition-colors"
            >
              Create Post
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {drafts.map((draft) => (
              <div
                key={draft.id}
                className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                  selectedDrafts.has(draft.id)
                    ? 'border-onrecord-orange'
                    : 'border-transparent hover:border-gray-300'
                }`}
                onClick={() => isSelectionMode && handleToggleSelect(draft.id)}
              >
                {/* Media Preview */}
                <div className="aspect-square bg-gray-100 relative">
                  <img
                    src={draft.mediaUrl}
                    alt="Draft"
                    className="w-full h-full object-cover"
                  />

                  {/* Media Type Badge */}
                  <div className="absolute top-2 right-2">
                    {draft.mediaType === 'video' ? (
                      <div className="bg-black bg-opacity-60 rounded-full p-1.5">
                        <Video className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <div className="bg-black bg-opacity-60 rounded-full p-1.5">
                        <ImageIcon className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Selection Checkbox */}
                  {isSelectionMode && (
                    <div className="absolute top-2 left-2">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedDrafts.has(draft.id)
                          ? 'bg-onrecord-orange border-onrecord-orange'
                          : 'bg-white bg-opacity-60 border-white'
                      }`}>
                        {selectedDrafts.has(draft.id) && (
                          <CheckCircle className="w-5 h-5 text-white" fill="currentColor" />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Hover Overlay */}
                  {!isSelectionMode && (
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleUploadDraft(draft.id)
                          }}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                          title="Upload"
                        >
                          <Upload className="w-5 h-5 text-onrecord-orange" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteDraft(draft.id)
                          }}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Draft Info */}
                <div className="p-3 bg-white">
                  <div className="flex items-center justify-between mb-1">
                    {getStatusBadge(draft)}
                    <span className="text-xs text-gray-500">{formatFileSize(draft.fileSize)}</span>
                  </div>

                  {draft.caption && (
                    <p className="text-xs text-gray-700 line-clamp-2 mb-1">
                      {draft.caption}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{formatDate(draft.createdAt)}</span>
                    {draft.location && (
                      <span className="text-xs text-gray-500">{draft.location.city}</span>
                    )}
                  </div>

                  {draft.error && (
                    <p className="text-xs text-red-600 mt-1 line-clamp-1">
                      {draft.error}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
